/* eslint-disable object-curly-spacing */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import Select from 'react-select';
import resolveConfig from 'tailwindcss/resolveConfig';
import { useForm, Controller } from 'react-hook-form';

import { contact } from '@app/lib/facebook-pixel';
import SEO from '@app/components/seo';
import Layout from '@app/layouts/page-default';

import tailwindConfig from '../tailwind.config';

const fullConfig = resolveConfig(tailwindConfig);

const ContactUsImageStyled = styled.div`
  ${tw`flex`}

  .backdrop-image {
    min-height: 30vh;
    background-image: url('/assets/images/bg_contact-us-min.jpg');
    background-size: cover;
    object-fit: cover;
    background-position: center center;

    @media (min-width: ${fullConfig.theme.screens.lg}) {
      min-height: 50vh;
    }

    &:after {
      ${tw`bg-gradient-to-t from-black z-0`};
      ${tw`block absolute w-full bottom-0 left-0`};
      content: '';
      height: 10vh;

      @media (min-width: ${fullConfig.theme.screens.lg}) {
        height: 60vh;
      }
    }
  }
`;

const ContactUsContentStyled = styled.div`
  ${tw`flex flex-col bg-black w-full`}

  .content-block {
    max-width: 1360px;
    font-size: 1.2rem;
    line-height: 1.5;
    @media (min-width: ${fullConfig.theme.screens.lg}) {
      font-size: 2rem;
      line-height: 3rem;
    }
  }

  .form-content {
    max-width: 1360px;
    .form-label {
      font-size: 16px;
      line-height: 24px;
    }

    .react-select__single-value {
      color: white;
    }

    @media (min-width: ${fullConfig.theme.screens.md}) and (max-width: ${fullConfig.theme.screens
        .lg}) {
      .email-input {
        grid-row: 2;
        grid-column: 1;
      }
      .phone-input {
        grid-row: 3;
        grid-column: 1;
      }

      .subject-input {
        grid-row: 1;
        grid-column: 2;
      }

      .message-input {
        grid-row-start: 2;
        grid-row-end: 4;
        grid-column: 2;

        textarea {
          height: calc(100% - 32px);
          box-sizing: border-box;
        }
      }
      .submit-input {
        grid-row: 4;
        grid-column: 2;
        align-self: flex-end;
        text-align: right;
      }
    }

    @media (min-width: ${fullConfig.theme.screens.md}) {
      .message-input {
        textarea {
          height: calc(100% - 32px);
          box-sizing: border-box;
        }
      }
      .submit-input {
        text-align: right;
      }
    }
  }
`;

type SelectItem = { label: string; value: string };

export type ContactFormType = {
  name: string;
  email: string;
  phoneNumber: string;
  subject: SelectItem;
  message: string;
  f: string;
};

const subjectOptions: SelectItem[] = [
  { label: 'General Inquiry', value: '1' },
  { label: 'Advertising Opportunity', value: '2' },
  { label: 'Movie distribution and exhibition', value: '3' },
  { label: 'Corporate booking', value: '4' },
  { label: 'Customer feedback', value: '5' },
];

export default function ContactUs(): JSX.Element {
  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactFormType>({
    defaultValues: {
      subject: subjectOptions[0],
    },
  });

  const onSubmit = async (values: ContactFormType) => {
    const response = await fetch('/api/contact/feedback', {
      method: 'POST',
      body: JSON.stringify({ ...values, subject: values.subject.label }),
    });
    const results = await response.json();
    if (results.success) {
      reset();
      toast(results.data.message, {
        type: 'success',
        autoClose: 3000,
      });
      contact({ ...values, subject: values.subject.label });
    } else {
      toast(results.error.message, {
        type: 'error',
        autoClose: 3000,
      });
    }
  };
  return (
    <Layout>
      <SEO title="Contact Us" />
      <ContactUsImageStyled>
        <div className="backdrop-image block w-full relative" />
      </ContactUsImageStyled>
      <ContactUsContentStyled>
        <div className="content-block px-8 py-12 mx-auto text-white font-bold">
          Whether you are a movie distributor, an aspiring producer or looking to advertise your
          brand at our cinemas, or simply feedback on your experience, we would love to hear from
          you.
        </div>
        <div className="form-content px-8 mx-auto w-full mb-8">
          <form
            className="grid lg:grid-cols-12 md:grid-cols-2 md:grid-rows-3 sm:grid-cols-1 gap-6"
            onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4 form-element name-input lg:col-start-1 lg:col-end-5">
              <label className="text-white form-label">
                <span className="label">Name</span>
                <input
                  className={`form-input mt-2 block w-full text-input ${
                    errors.name ? 'form-input-validation-error' : null
                  }`}
                  {...register('name', { required: 'Please enter a valid name' })}
                />
                {errors.name ? (
                  <span className="text-red-500 text-base mt-2 block">{errors.name.message}</span>
                ) : null}
              </label>
            </div>
            <div className="mb-4 form-element email-input lg:col-start-1 lg:col-span-4">
              <label className="text-white form-label">
                <span className="label">Email</span>
                <input
                  className={`form-input mt-2 block w-full text-input ${
                    errors.email ? 'form-input-validation-error' : null
                  }`}
                  {...register('email', {
                    required: 'Please enter a email address',
                    pattern: {
                      value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: 'Invalid email address',
                    },
                  })}
                />
                {errors.email ? (
                  <span className="text-red-500 text-base mt-2 block">{errors.email.message}</span>
                ) : null}
              </label>
            </div>
            <div className="mb-4 form-element phone-input lg:col-start-1 lg:col-span-4">
              <label className="text-white form-label">
                <span className="label">Phone number</span>
                <input
                  className={`form-input mt-2 block w-full text-input ${
                    errors.phoneNumber ? 'form-input-validation-error' : null
                  }`}
                  {...register('phoneNumber', {
                    required: false,
                    pattern: {
                      value: /^\d{1,14}$/,
                      message: 'Please enter a valid mobile number with country eg: (+9477xxxxxxx)',
                    },
                  })}
                />
                {errors.phoneNumber ? (
                  <span className="text-red-500 text-base mt-2 block">
                    {errors.phoneNumber.message}
                  </span>
                ) : null}
              </label>
            </div>
            <div className="mb-4 form-element subject-input lg:col-start-6 lg:col-span-4 lg:row-start-1">
              <label className="text-white form-label">
                <span className="label">Select your subject</span>
              </label>
              <Controller
                name="subject"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      options={subjectOptions}
                      className="kcc-select mt-2"
                      classNamePrefix="react-select"
                      {...field}
                      value={subjectOptions.find((item) => item.value === field.value.value)}
                    />
                    {error ? (
                      <span className="text-red-500 text-base mt-2 block">{error.message}</span>
                    ) : null}
                  </>
                )}
              />
            </div>
            <div className="mb-4 form-element message-input lg:col-start-6 lg:col-span-4 lg:row-start-2 lg:row-span-2">
              <label className="text-white form-label">
                <span className="label">Type your message here</span>
                <textarea
                  className={`text-textarea form-input mt-2 block w-full text-area ${
                    errors.message ? 'form-textarea-validation-error' : null
                  }`}
                  {...register('message', { required: 'Please enter a message' })}
                />
                {errors.message ? (
                  <span className="text-red-500 text-base mt-2 block">
                    {errors.message.message}
                  </span>
                ) : null}
              </label>
            </div>
            <div className="mb-4 form-element submit-input lg:col-start-6 lg:col-span-4 lg:row-start-4">
              <input className="hidden" type="text" {...register('f')} />
              <button type="submit" className="btn--blue-extra">
                Submit
              </button>
            </div>
          </form>
        </div>
      </ContactUsContentStyled>
    </Layout>
  );
}
